import { useEffect, useRef, useState } from 'react'
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { InvestCard2, KanbanCard2 } from 'components/index'
import { Label } from 'components/Cards/KanbanCard'
import useStorage from 'hooks/useStorage'
import { createPortal } from 'react-dom'

import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { DefaultCardType, ColumnType, InvestimentCardType } from '../../type'
import { useBoard } from '../../data/BoardProvider'
import { CardContainer } from './style'

type ICardProps = {
  item: DefaultCardType | InvestimentCardType
  column: ColumnType
  onTrash?: ((id: string) => void) | false
  investCard: boolean
  handleClick?: (..._param: any) => void
  index: number
  noSpecialist?: boolean
  notDraggable?: boolean
  isEnterprise?: boolean
  columnsWithTrash?: number[]
}

export const Item = ({
  item,
  column,
  onTrash,
  investCard,
  handleClick,
  index,
  noSpecialist,
  notDraggable,
  isEnterprise,
  columnsWithTrash,
}: ICardProps) => {
  const ref = useRef(null)
  const { moveCard } = useBoard()
  const [preview, setPreview] = useState<boolean>(false)
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 })

  useEffect(() => {
    const cardElement = ref.current

    if (!cardElement || notDraggable) {
      return
    }
    const cleanup = draggable({
      element: cardElement,
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          render() {
            const el = document.createElement('div')
            el.style.width = '1px'
            el.style.height = '1px'
            el.style.opacity = '0'

            document.body.appendChild(el)
            // Aqui vem a parte importante:
            nativeSetDragImage?.(el, 0, 0)

            return () => {
              document.body.removeChild(el)
            }
          },
        })
      },
      onDrag: ({ location }) => {
        // Atualiza a posição do preview enquanto o item está sendo arrastado
        const pointerPosition = location.current.input
        setPreview(true)
        setPreviewPosition({
          x: pointerPosition.pageX,
          y: pointerPosition.pageY,
        })
      },
      getInitialData() {
        return item
      },
      // Manda os dados do card para o drag-and-drop
      onDrop() {
        setPreview(false)
      },
    })

    return cleanup
  }, [item, moveCard, notDraggable])

  return (
    <>
      <CardContainer
        ref={ref}
        className="card-container"
        investCard={investCard}
        changeOpacity={preview}
        data-testid={`card-${item.name}`}
      >
        {investCard ? (
          <InvestCard2
            item={item}
            isEnterprise={isEnterprise}
            onClick={() =>
              handleClick &&
              handleClick(
                {
                  ...item,
                  column: {
                    id: column.id,
                    name: column.name,
                    icon: column.icon,
                  },
                },
                index
              )
            }
          />
        ) : (
          <KanbanCard2
            {...item}
            color="#ffffff"
            onTrash={columnsWithTrash?.includes(column.id) && onTrash}
            onClick={() =>
              handleClick &&
              handleClick({ ...item, columnId: column.id }, index)
            }
            id={item.id || item.contractId}
          >
            {!noSpecialist && (
              <Label
                id={item.specialist.id}
                content={item.specialist.name}
                icon={item.specialist.profilePicture}
              />
            )}
          </KanbanCard2>
        )}
      </CardContainer>
      {preview &&
        createPortal(
          <CardContainer
            ref={ref}
            className="card-container"
            investCard={investCard}
            data-testid={`card-${item.name}`}
            style={{
              position: 'absolute',
              left: previewPosition.x,
              top: previewPosition.y,
              transform: 'translate(-50%, -50%)',
              zIndex: 9999,
              pointerEvents: 'none',
              // userSelect: 'none',
              // webKitUser: 'none',
            }}
          >
            {investCard ? (
              <InvestCard2
                item={item}
                isEnterprise={isEnterprise}
                onClick={() =>
                  handleClick &&
                  handleClick(
                    {
                      ...item,
                      column: {
                        id: column.id,
                        name: column.name,
                        icon: column.icon,
                      },
                    },
                    index
                  )
                }
              />
            ) : (
              <KanbanCard2
                {...item}
                color="#ffffff"
                onTrash={columnsWithTrash?.includes(column.id) && onTrash}
                onClick={() =>
                  handleClick &&
                  handleClick({ ...item, columnId: column.id }, index)
                }
              >
                {!noSpecialist && (
                  <Label
                    id={item.specialist.id}
                    content={item.specialist.name}
                    icon={item.specialist.profilePicture}
                  />
                )}
              </KanbanCard2>
            )}
          </CardContainer>,
          document.body // Renderiza o preview diretamente no body com localização de acordo com a posição do ponteiro
        )}
    </>
  )
}
